<script setup>
import { userAuthState } from "@/@core/auth"
import { SurveyType } from "@/enums/survey/survey.type"
import RaiseTicketDialog from "@/views/survey/collection/RaiseTicketDialog.vue"
import TicketTable from "@/views/survey/ticket-management/TicketTable.vue"
import { useRoute, useRouter } from 'vue-router'
import { VBtn, VCardItem, VCardText } from "vuetify/lib/components/index.mjs"
import { VStepper, VStepperHeader, VStepperItem } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: "Survey Collection View",

    // subject: "service_desk.menu",
    // action: "dashboard",
  },
})

const route = useRoute()
const router = useRouter()

const surveyId = computed(() => route.params.survey)
const surveyType = computed(() => route.params.type ?? null)
const participantId = computed(() => route.params.participant ?? null)
const surveyScheduleSubscriptionId = computed(() => route.query.survey_schedule_subscription_id ?? null)

const defaultQuery = computed(() => {
  let query = {}

  // if (route.query.survey_schedule_subscription_id) {
  //   query = {
  //     ...query,
  //     survey_schedule_subscription_id: route.query.survey_schedule_subscription_id,
  //   }
  // }

  if (surveyType.value === SurveyType.CUSTOMER) {
    query = {
      ...query,
      episode_id: participantId.value,
    }
  } else {
    query = {
      ...query,
      participant_id: participantId.value,
    }
  }

  return query
})

const {
  data: surveyResponse,
} = await useApi(createUrl(`/survey/survey-responses/${surveyId.value}`, {
  query: {
    ...defaultQuery.value,
  },
}), {
  beforeFetch: () => {},
  onFetchError: error => {
    // router.push({
    //   name: 'invalid-url',
    //   params: { code: 404 },
    // })
  },
})

const currentStep = ref(0)

const generateFormURL = (id, id2) => {
  const link = import.meta.env.VITE_BASE_URL +'/survey-collect?survey=' + btoa(id) + '&subscription=' + btoa(id2)
  
  navigator.clipboard.writeText(link)

  console.log(link)
}

const getAnswer = (answers, questionId) => {
  const data = answers.find(x => x.question_id === questionId)
  if (!data) return '--'

  return data.question_option?.label ?? data.response ?? '--'
}

// const getTicketCount = (answers, questionId) => {
//   const data = answers.find(x => x.question_id === questionId)

//   return data?.tickets?.length ?? 0
// }

const exportLoading = ref(false)

const downloadSurveyResponse = async () => {
  exportLoading.value = true

  const url = createUrl('survey/survey-responses/export', {
    query: {
      search: {
        survey_id: surveyId.value,
        ...defaultQuery.value,
      },
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {})

  exportLoading.value = false
}

// Raise Ticket Dialog
const isRaiseTicketDialogOpen = ref(false)
const selectedQuestionAnswer = ref(null)

const openRaiseTicketDialog = (answer, question) => {
  selectedQuestionAnswer.value = {
    ...answer,
    question,
  }
  isRaiseTicketDialogOpen.value = true
}

const onTicketCreated = () => {
  // Refresh data or show success message
  // Can be extended to refetch survey response if needed
}

// Ticket Table Dialog
const isTicketTableDialogVisible = ref(false)
const ticketTableFilter = ref({})
const refTicketTable = ref()

const openTicketTableDialog = answer => {
  if (!answer?.id) return

  ticketTableFilter.value = {
    survey_question_answer_id: answer.id,
  }
  isTicketTableDialogVisible.value = true
}
</script>

<template>
  <VRow>
    <VCol
      md="8"
      cols="12"
      class="d-flex flex-column gap-6"
    >
      <template
        v-for="(subscription, subscriptionIndex) in surveyResponse.subscriptions"
        :key="subscriptionIndex"
      >
        <AppCardActions
          action-collapsed
          :collapsed="subscription.id != surveyScheduleSubscriptionId"
        >
          <template #title>
            <div>
              {{ subscription.schedule.name }}
            </div>
            <div class="d-flex align-center me-6 text-h6">
              {{ formatDate(subscription.schedule_at) }}
            </div>
          </template>
          <template #before-actions="{ data }">
            <div>
              <VChip
                class="me-2"
                :color="subscription.collected_at ? 'primary': 'warning'"
              >
                <span v-if="subscription.collected_at">
                  {{ formatDate(subscription.collected_at) ?? 'N/A' }}
                </span>
                <span v-else>
                  Not Collected
                </span>
              </VChip>
            </div>
            
            <!--
              <VBtn 
              size="small"
              color="primary"
              >
              Action Button
              </VBtn> 
            -->
          </template>

          <VDivider />

          <template v-if="subscription.collected_at">
            <VCardItem style="padding: 4px 24px !important;">
              <VStepper
                v-model="currentStep"
                show-actions
                class="pa-0"
                style="box-shadow: none !important;"
              >
                <template #default="{ prev, next }">
                  <VStepperHeader style="box-shadow: none !important;">
                    <template
                      v-for="(formStep, formStepIndex) in surveyResponse.form_steps"
                      :key="`${formStepIndex}-step`"
                    >
                      <VStepperItem
                        :value="formStepIndex"
                        :icon="formStep.icon"
                        :title="formStep.title"
                      />

                      <VDivider
                        v-if="formStepIndex + 1 !== surveyResponse.form_steps?.length"
                        :key="formStepIndex"
                      />
                    </template>
                  </VStepperHeader>
                </template>
              </VStepper>
            </VCardItem>
  
            
            <VCardText>
              <VWindow
                v-model="currentStep"
                style="min-block-size: 400px;"
                class="disable-tab-transition"
              >
                <template
                  v-for="(formStep, formStepIndex) in surveyResponse.form_steps"
                  :key="formStepIndex"
                >
                  <VWindowItem class="d-flex flex-column gap-4 bg-var-theme-white">
                    <div
                      v-for="(questionGroup, questionGroupIndex) in formStep.question_groups"
                      :key="questionGroupIndex"
                      class="px-3 py-3 border-sm rounded-lg"
                    >
                      <h6 class="text-base font-weight-medium text-primary">
                        {{ questionGroup.title }}
                      </h6>
                      <VDivider class="mt-2 mb-3" />
                              
                      <div
                        v-for="(question, questionIndex) in questionGroup.questions"
                        :key="questionIndex"
                      >
                        <p class="mb-2 text-body-1 font-weight-medium mt-1">
                          <span class="me-3 custom-background pa-1 rounded text-primary ">Q.{{ questionIndex + 1 }}  </span> {{ question.label }}
                        </p>
                        <div class="mb-4 text-body-1 px-1 py-3 rounded-lg custom-background text-high-emphasis border-sm">
                          <div class="d-flex align-center">
                            <div>
                              <span class="ms-1 me-2"> Ans:</span> {{ getAnswer(subscription.answers, question.id) }}
                            </div>
                            <VSpacer />
                            <VBtn
                              color="primary"
                              variant="tonal"
                              size="small"
                              class="mr-2"
                              prepend-icon="tabler-clipboard-plus"
                              @click="openRaiseTicketDialog(subscription.answers.find(a => a.question_id === question.id), question)"
                            >
                              Raise
                              <VTooltip
                                activator="parent"
                                location="bottom"
                              >
                                Raise A Ticket
                              </VTooltip>
                            </VBtn>
                            <VBtn
                              color="primary"
                              variant="tonal"
                              size="small"
                              class="mr-2"
                              prepend-icon="tabler-ticket"
                              @click="openTicketTableDialog(subscription.answers.find(a => a.question_id === question.id))"
                            >
                              <!-- {{ getTicketCount(subscription.answers, question.id) }} -->
                              View
                              <VTooltip
                                activator="parent"
                                location="bottom"
                              >
                                View Raised Tickets
                              </VTooltip>
                            </VBtn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </VWindowItem>
                </template>
              </VWindow>
            </VCardText>

            <VCardText>
              <div class="d-flex align-center">
                <VBtn
                  color="secondary"
                  variant="tonal"
                  prepend-icon="tabler-arrow-left"
                  :disabled="currentStep === 0"
                  @click="currentStep--"
                >
                  Previous
                </VBtn>

                <VSpacer />

                <VBtn 
                  color="secondary"
                  variant="tonal"
                  append-icon="tabler-arrow-right"
                  :disabled="currentStep === surveyResponse?.form_steps?.length - 1"
                  @click="currentStep ++"
                >
                  Continue
                </VBtn>
              </div>
            </VCardText>
          </template>
          <template v-else>
            <VCardText>
              <VAlert
                color="warning"
                variant="tonal"
                icon="tabler-alert-triangle"
              >
                Survey is not completed yet.
              </VAlert> 

              <VBtn
                class="mt-4"
                color="secondary"
                variant="tonal"
                @click="generateFormURL(surveyId, subscription.id)"
              >
                Get Link (For Testing Only)
              </VBtn>
            </VCardText>
          </template>
        </AppCardActions>
      </template>


      <!--
        <VCard v-if="surveyResponse.collected_at">
        <VCardItem>
        <VCardTitle>
        Survey Responses
        </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardItem style="padding: 4px 24px !important;">
        <VStepper
        v-model="currentStep"
        show-actions
        class="pa-0"
        style="box-shadow: none !important;"
        >
        <template #default="{ prev, next }">
        <VStepperHeader style="box-shadow: none !important;">
        <template
        v-for="(formStep, formStepIndex) in surveyResponse.survey.form_steps"
        :key="`${formStepIndex}-step`"
        >
        <VStepperItem
        :value="formStepIndex"
        :icon="formStep.icon"
        :title="formStep.title"
        />

        <VDivider
        v-if="formStepIndex + 1 !== surveyResponse.survey.form_steps?.length"
        :key="formStepIndex"
        />
        </template>
        </VStepperHeader>
        </template>
        </VStepper>
        </VCardItem>

        <VCardText>
        <VWindow
        v-model="currentStep"
        style="min-block-size: 400px;"
        class="disable-tab-transition"
        >
        <template
        v-for="(formStep, formStepIndex) in surveyResponse.survey.form_steps"
        :key="formStepIndex"
        >
        <VWindowItem class="d-flex flex-column gap-4 bg-var-theme-white">
        <div
        v-for="(questionGroup, questionGroupIndex) in formStep.question_groups"
        :key="questionGroupIndex"
        class="px-3 py-3 border-sm rounded-lg"
        >
        <h6 class="text-base font-weight-medium text-primary">
        {{ questionGroup.title }}
        </h6>
        <VDivider class="mt-2 mb-3" />
                              
        <div
        v-for="(question, questionIndex) in questionGroup.questions"
        :key="questionIndex"
        >
        <p class="mb-2 text-body-1 font-weight-medium mt-1">
        <span class="me-3 custom-background pa-1 rounded text-primary ">Q.{{ questionIndex + 1 }}  </span> {{ question.label }}
        </p>
        <p class="mb-4 text-body-1 px-1 py-3 rounded-lg custom-background text-high-emphasis border-sm">
        <span class="ms-1 me-2"> Ans:</span> {{ question?.answer?.response ?? '--' }}
        </p>
        </div>
        </div>
        </VWindowItem>
        </template>
        </VWindow>
        </VCardText>

        <VCardText>
        <div class="d-flex align-center">
        <VBtn
        color="secondary"
        variant="tonal"
        prepend-icon="tabler-arrow-left"
        :disabled="currentStep === 0"
        @click="currentStep--"
        >
        Previous
        </VBtn>

        <VSpacer />

        <VBtn 
        color="secondary"
        variant="tonal"
        append-icon="tabler-arrow-right"
        :disabled="currentStep === surveyResponse.survey?.form_steps?.length - 1"
        @click="currentStep ++"
        >
        Continue
        </VBtn>
        </div>
        </VCardText>
        </VCard>
        <VAlert
        v-else
        color="warning"
        variant="tonal"
        icon="tabler-alert-triangle"
        >
        Survey is not completed yet.
        </VAlert> 
      -->
    </VCol>
    <VCol
      md="4"
      cols="12"
      class="d-flex flex-column gap-6"
    >
      <VBtn
        :disabled="!$can('export', 'survey.survey')"
        :loading="exportLoading"
        @click="downloadSurveyResponse"
      >
        Export
      </VBtn>

      <VCard>
        <VCardItem>
          <VCardTitle>
            Participant
          </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardText>
          <VRow
            v-if="surveyResponse.type === SurveyType.CUSTOMER"
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> UHID</span> : {{ surveyResponse.episode.customer.uhid || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Name</span> : {{ surveyResponse.episode.customer.name || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Phone No</span> : {{ surveyResponse.episode.customer.phone || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Email</span> : {{ surveyResponse.episode.customer.email || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Unit</span> : {{ surveyResponse.episode.unit.name || 'N/A' }}
            </VCol>
          </VRow>

          <VRow
            v-if="surveyResponse.type === SurveyType.EMPLOYEE || surveyResponse.type === SurveyType.EVENT"
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Name</span> : {{ surveyResponse.participant.name }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Phone No</span> : {{ surveyResponse.participant.phone || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Email</span> : {{ surveyResponse.participant.email || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Unit</span> : {{ surveyResponse.participant.unit.name || 'N/A' }}
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <VCard>
        <VCardItem>
          <VCardTitle>
            Survey
          </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardText>
          <VRow
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Title</span> : {{ surveyResponse?.title }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Description</span> : {{ surveyResponse?.description }}
            </VCol>
          </VRow>
        </VCardText>

        <VCardText>
          <VBtn
            :disabled="!$can('read', 'survey.survey')"
            variant="flat"
            block
            append-icon="tabler-external-link"
            @click="router.push({ name: 'app-survey-customer-setup', params: { type: surveyResponse.type, survey: surveyResponse.id } })"
          >
            View Survey
          </VBtn>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <!-- Raise Ticket Dialog -->
  <RaiseTicketDialog
    v-model:is-dialog-open="isRaiseTicketDialogOpen"
    :question-answer="selectedQuestionAnswer"
    @ticket-created="onTicketCreated"
  />

  <!-- Ticket Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="isTicketTableDialogVisible = false"
    />

    <VCard>
      <VCardText class="pa-0">
        <TicketTable
          ref="refTicketTable"
          :ticket-filters="ticketTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'outlined',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
